import React from 'react';
import { AgentCustomRule, AgentModule, EmailReplyTemplate, RuleOperator } from '../../types';
import { evaluateCustomRules, ruleCatalog, ruleTemplates, validateCustomRules } from '../../services/advisoryRules';
import { ReplyTemplateManager } from './ReplyTemplateManager';
import { GoldExamplesManager } from './GoldExamplesManager';
import { adminService } from '../../services/adminService';

const agentModuleOptions: AgentModule[] = ['QUOTER', 'SCHEDULED', 'PLANNER', 'FUEL', 'WHATSAPP', 'EMAIL'];

const moduleLabel = (module: AgentModule) => {
  if (module === 'QUOTER') return 'Charter';
  if (module === 'SCHEDULED') return 'Scheduled';
  if (module === 'PLANNER') return 'Dispatch';
  if (module === 'FUEL') return 'Fuel';
  if (module === 'EMAIL') return 'Email';
  return 'WhatsApp';
};

const charterSectionOptions = ['MISSION', 'ROUTE', 'TIMING', 'SERVICES', 'PRICING', 'DISCOUNT', 'FINAL'] as const;

type AgentTabProps = {
  agentSettings: any;
  setAgentSettings: (next: any) => void;
  activeAgentModule: AgentModule;
  setActiveAgentModule: (m: AgentModule) => void;
  agentRoles: readonly string[];
  customRuleDraft: AgentCustomRule;
  setCustomRuleDraft: (rule: AgentCustomRule) => void;
  ruleTestResult: string;
  setRuleTestResult: (v: string) => void;
  customRulesJsonDraft: string;
  setCustomRulesJsonDraft: (v: string) => void;
  customRulesJsonError: string | null;
  setCustomRulesJsonError: (v: string | null) => void;
  agentHistory: any[];
  onRollbackSnapshot: (id: string) => void;
};

export const AgentTab: React.FC<AgentTabProps> = ({
  agentSettings,
  setAgentSettings,
  activeAgentModule,
  setActiveAgentModule,
  agentRoles,
  customRuleDraft,
  setCustomRuleDraft,
  ruleTestResult,
  setRuleTestResult,
  customRulesJsonDraft,
  setCustomRulesJsonDraft,
  customRulesJsonError,
  setCustomRulesJsonError,
  agentHistory,
  onRollbackSnapshot,
}) => {
  const [polishAnalyticsLoading, setPolishAnalyticsLoading] = React.useState(false);
  const [polishAnalytics, setPolishAnalytics] = React.useState<{
    days: number;
    totals: {
      totalPolishUses: number;
      totalSentReplies: number;
      polishPercentOfSentReplies: number;
      sentAfterPolishRate: number;
      editedAfterPolishRate: number;
    };
    byMode: Array<{ mode: string; count: number }>;
    byOperator: Array<{ userId: string; name: string; email?: string | null; count: number; sentAfterPolish: number; editedAfterPolish: number }>;
  } | null>(null);
  React.useEffect(() => {
    if (activeAgentModule !== 'EMAIL') return;
    let active = true;
    setPolishAnalyticsLoading(true);
    adminService.getEmailPolishAnalytics(30)
      .then((data) => { if (active) setPolishAnalytics(data); })
      .catch(() => { if (active) setPolishAnalytics(null); })
      .finally(() => { if (active) setPolishAnalyticsLoading(false); });
    return () => { active = false; };
  }, [activeAgentModule]);

  const commKey = activeAgentModule === 'EMAIL' ? 'email' : activeAgentModule === 'WHATSAPP' ? 'whatsapp' : null;
  const channelLabel = commKey === 'email' ? 'Email' : 'WhatsApp';
  const wa = commKey ? agentSettings.communications?.[commKey] : null;
  const setWa = (patch: Record<string, any>) => {
    if (!commKey) return;
    setAgentSettings({
      ...agentSettings,
      communications: { ...agentSettings.communications, [commKey]: { ...wa, ...patch } },
    });
  };
  const setWaSection = (section: string, patch: Record<string, any>) => setWa({
    [section]: { ...(wa as any)?.[section], ...patch },
  });
  const emailTemplates: EmailReplyTemplate[] = activeAgentModule === 'EMAIL'
    ? ((agentSettings?.communications?.email?.replyIntelligence?.templates || []) as EmailReplyTemplate[])
    : [];

  return (
  <div className="grid grid-cols-1 2xl:grid-cols-[1.25fr_0.75fr] gap-8 animate-in slide-in-from-bottom-4">
    <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10 space-y-10 min-w-0">
      <div className="flex items-center justify-between border-b border-slate-50 pb-6">
        <div>
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em]">Supervisor Agent Control</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Per-module training & governance</p>
        </div>
        <button
          onClick={() => setAgentSettings({ ...agentSettings, enabled: !agentSettings.enabled })}
          className={`px-6 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
            agentSettings.enabled ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100' : 'bg-slate-100 text-slate-400'
          }`}
        >
          {agentSettings.enabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {agentModuleOptions.map((module) => (
          <button
            key={module}
            onClick={() => setActiveAgentModule(module)}
            className={`px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
              activeAgentModule === module
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'
                : 'bg-slate-50 text-slate-400'
            }`}
          >
            {moduleLabel(module)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Advisory Mode</label>
          <select
            value={agentSettings.advisoryMode}
            onChange={(e) => setAgentSettings({ ...agentSettings, advisoryMode: e.target.value as any })}
            className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500/20 outline-none appearance-none"
          >
            <option value="INLINE">Inline Hints</option>
            <option value="PANEL">Side Panel</option>
            <option value="TOAST">Toasts</option>
          </select>
        </div>
        <div className="space-y-3">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Advisory Policy</label>
          <button
            onClick={() => setAgentSettings({ ...agentSettings, advisoryOnly: !agentSettings.advisoryOnly })}
            className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
              agentSettings.advisoryOnly ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-slate-100 text-slate-400'
            }`}
          >
            {agentSettings.advisoryOnly ? 'Advisory Only' : 'Enforced'}
          </button>
        </div>
      </div>

      <div className="bg-slate-50 rounded-[2rem] p-6 border border-slate-100 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Module</p>
            <p className="text-lg font-black text-slate-800">{moduleLabel(activeAgentModule)}</p>
          </div>
          <button
            onClick={() =>
              setAgentSettings({
                ...agentSettings,
                modules: { ...agentSettings.modules, [activeAgentModule]: !agentSettings.modules[activeAgentModule] },
              })
            }
            className={`px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
              agentSettings.modules[activeAgentModule]
                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100'
                : 'bg-white text-slate-400 border border-slate-200'
            }`}
          >
            {agentSettings.modules[activeAgentModule] ? 'Module On' : 'Module Off'}
          </button>
        </div>

        <div className="space-y-3">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Role Access</p>
          <div className="flex flex-wrap gap-2">
            {agentRoles.map(role => {
              const enabled = agentSettings.rolesByModule?.[activeAgentModule]?.includes(role) ?? true;
              return (
                <button
                  key={`${activeAgentModule}-${role}`}
                  onClick={() => {
                    const current = agentSettings.rolesByModule?.[activeAgentModule] || [];
                    const next = enabled ? current.filter((r: string) => r !== role) : [...current, role];
                    setAgentSettings({
                      ...agentSettings,
                      rolesByModule: { ...agentSettings.rolesByModule, [activeAgentModule]: next },
                    });
                  }}
                  className={`px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest border transition-all ${
                    enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-400 border-slate-200'
                  }`}
                >
                  {role.replace('_', ' ')}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {commKey ? (
        <div className="rounded-[2rem] border border-sky-100 bg-[linear-gradient(180deg,#f7fbff_0%,#ffffff_100%)] p-6 space-y-6">

          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-sky-500">{channelLabel} Copilot</p>
              <h4 className="mt-2 text-lg font-black text-slate-900">Conversation supervision & reply guidance</h4>
              <p className="mt-1 text-sm font-medium text-slate-500">Control draft review, context lookups, thread intelligence, and reply supervision for operators in {channelLabel} workflows.</p>
            </div>
            <button
              onClick={() => setWa({ enabled: !wa?.enabled })}
              className={`shrink-0 px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
                wa?.enabled ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100' : 'bg-white text-slate-400 border border-slate-200'
              }`}
            >
              {wa?.enabled ? 'Copilot On' : 'Copilot Off'}
            </button>
          </div>

          {/* Copilot Persona — full width */}
          <div className="rounded-[1.5rem] border border-indigo-100 bg-[linear-gradient(160deg,#f8faff_0%,#ffffff_100%)] p-5 space-y-4">
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-indigo-500">Copilot Persona</p>
              <p className="mt-1 text-sm font-medium text-slate-500">Describe who this agent is, what it knows about your airline, and how it should speak. This is sent directly to the LLM as its identity and training. The more specific, the better it performs.</p>
            </div>
            <textarea
              rows={6}
              value={wa?.persona || ''}
              onChange={(e) => setWa({ persona: e.target.value })}
              placeholder={commKey === 'email'
                ? `Example:\nYou are the Email sales copilot for Miracle Air.\nTone: concise, professional, premium.\nNever commit pricing from memory — always reference system quote.\nAlways include a clear next step and timeline.\nIf details are missing, ask the single most important question.\nKeep reply compact unless detailed breakdown is requested.`
                : `Example:\nYou are the WhatsApp sales copilot for Miracle Air.\nTone: direct, warm, professional — no email formalities, no "Dear" or "Regards".\nKey routes: Honiara, Munda, Gizo, Seghe, Ramata, Choiseul Bay, Sola, Fera.\nNever quote pricing from memory — always reference the system quote.\nIf a client is frustrated, acknowledge first, then fix.\nAlways ask only the single most important missing question per reply.\nMax 3 sentences unless quoting a price breakdown.`}
              className="w-full bg-white border border-indigo-100 rounded-2xl p-4 text-sm font-medium text-slate-800 outline-none resize-none focus:ring-2 focus:ring-indigo-200/60 placeholder:text-slate-300 placeholder:font-normal leading-relaxed"
            />
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-300">Leave blank to use the default copilot identity</p>
          </div>

          {commKey === 'email' ? (
            <>
              <ReplyTemplateManager
                templates={emailTemplates}
                setTemplates={(templates) => {
                  setWa({
                    replyIntelligence: {
                      ...((wa as any)?.replyIntelligence || {}),
                      templates,
                    },
                  });
                }}
              />
              <GoldExamplesManager />
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Polish Analytics</p>
                    <p className="text-sm font-semibold text-slate-700">Usage of “Polish My Reply”</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      setPolishAnalyticsLoading(true);
                      const next = await adminService.getEmailPolishAnalytics(30);
                      setPolishAnalytics(next);
                      setPolishAnalyticsLoading(false);
                    }}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600"
                  >
                    Refresh
                  </button>
                </div>
                {polishAnalyticsLoading ? (
                  <div className="text-xs text-slate-500">Loading analytics…</div>
                ) : polishAnalytics ? (
                  <div className="space-y-3 text-xs text-slate-700">
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                      <div className="rounded-xl border border-slate-200 p-2"><div className="text-[10px] text-slate-400">Total Uses</div><div className="font-bold">{polishAnalytics.totals.totalPolishUses}</div></div>
                      <div className="rounded-xl border border-slate-200 p-2"><div className="text-[10px] text-slate-400">By Sent %</div><div className="font-bold">{polishAnalytics.totals.polishPercentOfSentReplies}%</div></div>
                      <div className="rounded-xl border border-slate-200 p-2"><div className="text-[10px] text-slate-400">Sent After %</div><div className="font-bold">{polishAnalytics.totals.sentAfterPolishRate}%</div></div>
                      <div className="rounded-xl border border-slate-200 p-2"><div className="text-[10px] text-slate-400">Edited After %</div><div className="font-bold">{polishAnalytics.totals.editedAfterPolishRate}%</div></div>
                      <div className="rounded-xl border border-slate-200 p-2"><div className="text-[10px] text-slate-400">Window</div><div className="font-bold">{polishAnalytics.days}d</div></div>
                    </div>
                    <div>
                      <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-slate-400">By Mode</div>
                      <div className="flex flex-wrap gap-2">
                        {polishAnalytics.byMode.map(row => (
                          <span key={row.mode} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold">
                            {row.mode}: {row.count}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-slate-400">By Operator</div>
                      <div className="max-h-48 overflow-auto rounded-xl border border-slate-200">
                        {polishAnalytics.byOperator.map(row => (
                          <div key={row.userId} className="flex items-center justify-between border-b border-slate-100 px-3 py-2 text-[11px] last:border-b-0">
                            <span className="truncate pr-3">{row.name}</span>
                            <span className="font-semibold">{row.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">No polish analytics data yet.</div>
                )}
              </div>
            </>
          ) : null}

          {/* 2-column grid: Draft Guardian + Intelligence stack */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

            {/* Draft Guardian */}
            <div className="space-y-4 rounded-[1.5rem] border border-slate-200 bg-white p-5">
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Draft Guardian</p>
                <p className="mt-1 text-sm font-medium text-slate-500">Review every outbound draft for tone and SOP compliance before it is sent.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="space-y-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Draft Policy</span>
                  <select
                    value={wa?.draftGuardian?.mode || 'WARN'}
                    onChange={(e) => setWaSection('draftGuardian', { mode: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none appearance-none"
                  >
                    <option value="ASSIST">Assist Only</option>
                    <option value="WARN">Warn Before Send</option>
                    <option value="BLOCK">Block Serious Violations</option>
                  </select>
                </label>
                <label className="space-y-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Block Severities</span>
                  <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    {(['LOW', 'MEDIUM', 'HIGH'] as const).map(level => {
                      const on = (wa?.draftGuardian?.blockSeverities || []).includes(level);
                      return (
                        <button
                          key={level}
                          onClick={() => {
                            const cur = wa?.draftGuardian?.blockSeverities || [];
                            setWaSection('draftGuardian', { blockSeverities: on ? cur.filter((s: string) => s !== level) : [...cur, level] });
                          }}
                          className={`px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest border ${on ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-white text-slate-400 border-slate-200'}`}
                        >
                          {level}
                        </button>
                      );
                    })}
                  </div>
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {([['enabled', 'Guard Drafts'], ['requireProfessionalTone', 'Premium Tone'], ['suggestRewrite', 'Suggest Rewrite']] as const).map(([field, label]) => {
                  const on = !!(wa?.draftGuardian as any)?.[field];
                  return (
                    <button
                      key={field}
                      onClick={() => setWaSection('draftGuardian', { [field]: !on })}
                      className={`px-4 py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest border transition-all ${on ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-400 border-slate-200'}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <textarea
                rows={3}
                value={(wa?.draftGuardian?.preferredPhrases || []).join('\n')}
                onChange={(e) => setWaSection('draftGuardian', { preferredPhrases: e.target.value.split('\n').map((v: string) => v.trim()).filter(Boolean) })}
                placeholder="Preferred premium phrases, one per line"
                className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none resize-none"
              />
              <textarea
                rows={3}
                value={(wa?.draftGuardian?.forbiddenPhrases || []).join('\n')}
                onChange={(e) => setWaSection('draftGuardian', { forbiddenPhrases: e.target.value.split('\n').map((v: string) => v.trim()).filter(Boolean) })}
                placeholder="Forbidden phrases, one per line"
                className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none resize-none"
              />
            </div>

            {/* Right column: 3 stacked sub-sections */}
            <div className="space-y-4">

              {/* Context Assistant */}
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Context Assistant</p>
                    <p className="mt-0.5 text-xs font-medium text-slate-400">Lookups and suggestions the agent can perform during live conversations.</p>
                  </div>
                  <button
                    onClick={() => setWaSection('contextAssistant', { enabled: !wa?.contextAssistant?.enabled })}
                    className={`shrink-0 px-4 py-2.5 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all border ${wa?.contextAssistant?.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
                  >
                    {wa?.contextAssistant?.enabled ? 'On' : 'Off'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {([['suggestActions', 'Suggest Actions'], ['allowEstimateLookup', 'Estimate Lookup'], ['allowQuoteLookup', 'Quote Lookup'], ['allowCustomerHistoryLookup', 'Customer History']] as const).map(([field, label]) => {
                    const on = !!(wa?.contextAssistant as any)?.[field];
                    return (
                      <button key={field} onClick={() => setWaSection('contextAssistant', { [field]: !on })} className={`px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border transition-all ${on ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <label className="block space-y-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                    Context Depth — {wa?.intelligence?.contextDepth || 15} messages
                  </span>
                  <input
                    type="range" min={5} max={30} step={1}
                    value={wa?.intelligence?.contextDepth || 15}
                    onChange={(e) => setWaSection('intelligence', { contextDepth: Number(e.target.value) })}
                    className="w-full accent-indigo-500"
                  />
                  <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-slate-300 px-0.5">
                    <span>5 min</span><span>30 max</span>
                  </div>
                </label>
              </div>

              {/* Thread Intelligence */}
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Thread Intelligence</p>
                    <p className="mt-0.5 text-xs font-medium text-slate-400">Summaries, task extraction, auto-fire triggers, and sentiment awareness.</p>
                  </div>
                  <button
                    onClick={() => setWaSection('intelligence', { enabled: !wa?.intelligence?.enabled })}
                    className={`shrink-0 px-4 py-2.5 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all border ${wa?.intelligence?.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
                  >
                    {wa?.intelligence?.enabled ? 'On' : 'Off'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {([
                    ['generateSummary', 'Generate Summary'],
                    ['extractTasks', 'Extract Tasks'],
                    ['extractReferences', 'Extract References'],
                    ['autoFireOnRoute', 'Auto-fire on Route'],
                    ['threadBriefOnOpen', 'Brief on Open'],
                    ['sentimentDetection', 'Sentiment Detection'],
                  ] as const).map(([field, label]) => {
                    const on = !!(wa?.intelligence as any)?.[field];
                    return (
                      <button key={field} onClick={() => setWaSection('intelligence', { [field]: !on })} className={`px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border transition-all ${on ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Reply Watcher */}
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Reply Watcher</p>
                    <p className="mt-0.5 text-xs font-medium text-slate-400">Detect missed replies and track promised follow-ups in open threads.</p>
                  </div>
                  <button
                    onClick={() => setWaSection('replyWatcher', { enabled: !wa?.replyWatcher?.enabled })}
                    className={`shrink-0 px-4 py-2.5 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all border ${wa?.replyWatcher?.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
                  >
                    {wa?.replyWatcher?.enabled ? 'On' : 'Off'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {([['detectMissedReplies', 'Detect Missed Replies'], ['detectBrokenPromises', 'Track Promised Follow-up']] as const).map(([field, label]) => {
                    const on = !!(wa?.replyWatcher as any)?.[field];
                    return (
                      <button key={field} onClick={() => setWaSection('replyWatcher', { [field]: !on })} className={`px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border transition-all ${on ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <label className="block space-y-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Reminder Minutes</span>
                  <input
                    type="number" min={1}
                    value={wa?.replyWatcher?.reminderMinutes || 15}
                    onChange={(e) => setWaSection('replyWatcher', { reminderMinutes: Math.max(1, Number(e.target.value || 15)) })}
                    className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
                  />
                </label>
              </div>

            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Enforcement (Module)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(['LOW', 'MEDIUM', 'HIGH'] as const).map(level => {
              const currentPolicy = agentSettings.enforcementByModule?.[activeAgentModule] || agentSettings.enforcement;
              const enabled = currentPolicy?.blockSeverities?.includes(level) ?? false;
              return (
                <button
                  key={level}
                  onClick={() => {
                    const current = currentPolicy?.blockSeverities || [];
                    const next = enabled ? current.filter((v: string) => v !== level) : [...current, level];
                    setAgentSettings({
                      ...agentSettings,
                      enforcementByModule: {
                        ...agentSettings.enforcementByModule,
                        [activeAgentModule]: {
                          ...currentPolicy,
                          blockSeverities: next,
                        },
                      },
                    });
                  }}
                  className={`px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border transition-all ${
                    enabled ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-white text-slate-400 border-slate-200'
                  }`}
                >
                  Block {level}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(() => {
              const currentPolicy = agentSettings.enforcementByModule?.[activeAgentModule] || agentSettings.enforcement;
              return (
                <>
                  <button
                    onClick={() =>
                      setAgentSettings({
                        ...agentSettings,
                        enforcementByModule: {
                          ...agentSettings.enforcementByModule,
                          [activeAgentModule]: {
                            ...currentPolicy,
                            allowOverride: !currentPolicy.allowOverride,
                          },
                        },
                      })
                    }
                    className={`px-6 py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
                      currentPolicy.allowOverride
                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                        : 'bg-slate-50 text-slate-400 border border-slate-100'
                    }`}
                  >
                    Allow Override
                  </button>
                  <button
                    onClick={() =>
                      setAgentSettings({
                        ...agentSettings,
                        enforcementByModule: {
                          ...agentSettings.enforcementByModule,
                          [activeAgentModule]: {
                            ...currentPolicy,
                            requireOverrideReason: !currentPolicy.requireOverrideReason,
                          },
                        },
                      })
                    }
                    className={`px-6 py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
                      currentPolicy.requireOverrideReason
                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                        : 'bg-slate-50 text-slate-400 border border-slate-100'
                    }`}
                  >
                    Require Override Reason
                  </button>
                  <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Override Roles</p>
                    <div className="flex flex-wrap gap-2">
                      {agentRoles.map(role => {
                        const allowed = (currentPolicy.overrideRoles || []).includes(role);
                        return (
                          <button
                            key={`override-${role}`}
                            onClick={() => {
                              const current = currentPolicy.overrideRoles || [];
                              const next = allowed ? current.filter((r: string) => r !== role) : [...current, role];
                              setAgentSettings({
                                ...agentSettings,
                                enforcementByModule: {
                                  ...agentSettings.enforcementByModule,
                                  [activeAgentModule]: {
                                    ...currentPolicy,
                                    overrideRoles: next,
                                  },
                                },
                              });
                            }}
                            className={`px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest border ${
                              allowed ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'
                            }`}
                          >
                            {role.replace('_', ' ')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Training Notes (Module)</p>
          <textarea
            rows={6}
            value={agentSettings.knowledgeBaseNotesByModule?.[activeAgentModule] || ''}
            onChange={(e) =>
              setAgentSettings({
                ...agentSettings,
                knowledgeBaseNotesByModule: {
                  ...agentSettings.knowledgeBaseNotesByModule,
                  [activeAgentModule]: e.target.value,
                },
              })
            }
            placeholder="Paste SOP notes, policy IDs, or examples specific to this module"
            className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none"
          />
        </div>
      </div>

      <div className="space-y-4">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Rules (Toggle On/Off)</p>
        <div className="grid grid-cols-1 gap-3">
          {ruleCatalog[activeAgentModule].map(rule => {
            const enabled = agentSettings.ruleToggles?.[activeAgentModule]?.[rule.id];
            const isOn = enabled === undefined ? true : enabled;
            return (
              <button
                key={rule.id}
                onClick={() =>
                  setAgentSettings({
                    ...agentSettings,
                    ruleToggles: {
                      ...agentSettings.ruleToggles,
                      [activeAgentModule]: {
                        ...(agentSettings.ruleToggles?.[activeAgentModule] || {}),
                        [rule.id]: !isOn,
                      },
                    },
                  })
                }
                className={`w-full text-left px-5 py-4 rounded-2xl border transition-all ${
                  isOn ? 'bg-white border-emerald-200' : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-slate-700">{rule.title}</p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{rule.description}</p>
                  </div>
                  <span className={`text-[9px] font-black uppercase tracking-widest ${
                    isOn ? 'text-emerald-600' : 'text-slate-400'
                  }`}>
                    {isOn ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-4">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Custom Rule Builder</p>
        <div className="space-y-3">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Templates</p>
          <div className="flex flex-wrap gap-2">
            {ruleTemplates[activeAgentModule].map(tpl => (
              <button
                key={tpl.id}
                onClick={() => {
                  const current = agentSettings.customRulesByModule?.[activeAgentModule] || [];
                  setAgentSettings({
                    ...agentSettings,
                    customRulesByModule: {
                      ...agentSettings.customRulesByModule,
                      [activeAgentModule]: [...current, { ...tpl, id: `${tpl.id}.${Date.now()}` }],
                    },
                  });
                }}
                className="px-4 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest border bg-indigo-50 text-indigo-700 border-indigo-200"
              >
                Add {tpl.title}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Rule title"
            value={customRuleDraft.title}
            onChange={(e) => setCustomRuleDraft({ ...customRuleDraft, title: e.target.value })}
            className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
          />
          {activeAgentModule === 'QUOTER' ? (
            <select
              value={customRuleDraft.section || 'ROUTE'}
              onChange={(e) => setCustomRuleDraft({ ...customRuleDraft, section: e.target.value })}
              className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
            >
              {charterSectionOptions.map(section => (
                <option key={section} value={section}>{section}</option>
              ))}
            </select>
          ) : null}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <select
            value={customRuleDraft.severity}
            onChange={(e) => setCustomRuleDraft({ ...customRuleDraft, severity: e.target.value as any })}
            className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
          >
            <option value="LOW">LOW</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="HIGH">HIGH</option>
          </select>
          <select
            value={customRuleDraft.logic || 'all'}
            onChange={(e) => setCustomRuleDraft({ ...customRuleDraft, logic: e.target.value as any })}
            className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
          >
            <option value="all">All conditions (AND)</option>
            <option value="any">Any condition (OR)</option>
          </select>
        </div>
        <div className="space-y-3">
          {customRuleDraft.conditions.map((cond, idx) => (
            <div key={`cond-${idx}`} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Field path (e.g. legs.length, discount)"
                value={cond.field || ''}
                onChange={(e) => {
                  const next = [...customRuleDraft.conditions];
                  next[idx] = { ...next[idx], field: e.target.value };
                  setCustomRuleDraft({ ...customRuleDraft, conditions: next });
                }}
                className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
              />
              <select
                value={cond.op || 'eq'}
                onChange={(e) => {
                  const next = [...customRuleDraft.conditions];
                  next[idx] = { ...next[idx], op: e.target.value as RuleOperator };
                  setCustomRuleDraft({ ...customRuleDraft, conditions: next });
                }}
                className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
              >
                {(['eq','neq','gt','gte','lt','lte','in','notIn','contains','notEmpty','empty'] as RuleOperator[]).map(op => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder='Value (JSON allowed, e.g. 0 or ["A","B"])'
                value={String(cond.value ?? '')}
                onChange={(e) => {
                  const next = [...customRuleDraft.conditions];
                  next[idx] = { ...next[idx], value: e.target.value };
                  setCustomRuleDraft({ ...customRuleDraft, conditions: next });
                }}
                className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Detail message"
            value={customRuleDraft.detail}
            onChange={(e) => setCustomRuleDraft({ ...customRuleDraft, detail: e.target.value })}
            className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
          />
          <input
            type="text"
            placeholder="Suggestion (optional)"
            value={customRuleDraft.suggestion || ''}
            onChange={(e) => setCustomRuleDraft({ ...customRuleDraft, suggestion: e.target.value })}
            className="w-full bg-white border border-slate-200 rounded-2xl p-4 font-bold text-slate-800 outline-none"
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const next = [...(customRuleDraft.conditions || [])];
              next.push({ field: '', op: 'eq', value: '' });
              setCustomRuleDraft({ ...customRuleDraft, conditions: next });
            }}
            className="px-4 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest bg-slate-100 text-slate-500"
          >
            + Add Condition
          </button>
          {customRuleDraft.conditions.length > 1 && (
            <button
              onClick={() => {
                const next = [...customRuleDraft.conditions];
                next.pop();
                setCustomRuleDraft({ ...customRuleDraft, conditions: next });
              }}
              className="px-4 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest bg-slate-100 text-slate-500"
            >
              − Remove Condition
            </button>
          )}
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              const parseValue = (raw: any) => {
                if (raw === undefined || raw === null || raw === '') return raw;
                try {
                  return JSON.parse(raw);
                } catch {
                  return raw;
                }
              };
              const normalizedConditions = (customRuleDraft.conditions || []).map(c => ({
                field: c.field || '',
                op: c.op || 'eq',
                value: parseValue(c.value),
              }));
              const newRule: AgentCustomRule = {
                ...customRuleDraft,
                id: customRuleDraft.id || `custom.${activeAgentModule.toLowerCase()}.${Date.now()}`,
                conditions: normalizedConditions,
                section: activeAgentModule === 'QUOTER' ? (customRuleDraft.section || 'ROUTE') : customRuleDraft.section,
              };
              const current = agentSettings.customRulesByModule?.[activeAgentModule] || [];
              setAgentSettings({
                ...agentSettings,
                customRulesByModule: {
                  ...agentSettings.customRulesByModule,
                  [activeAgentModule]: [...current, newRule],
                },
              });
              setCustomRuleDraft({
                id: '',
                title: '',
                severity: 'MEDIUM',
                detail: '',
                suggestion: '',
                section: activeAgentModule === 'QUOTER' ? 'ROUTE' : undefined,
                enabled: true,
                logic: 'all',
                conditions: [{ field: '', op: 'eq', value: '' }],
              });
            }}
            className="px-6 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest bg-indigo-600 text-white shadow-lg shadow-indigo-100"
          >
            Add Rule
          </button>
          <button
            onClick={() => {
              try {
                const stored = localStorage.getItem(`ma_agent_context_${activeAgentModule}`);
                let context: any = null;
                if (stored) {
                  context = JSON.parse(stored);
                } else {
                  const testInput = prompt('Paste JSON context to test against this rule (e.g. {"discount":20,"totals":{"legsCount":0}}):');
                  if (!testInput) return;
                  context = JSON.parse(testInput);
                }
                const parseValue = (raw: any) => {
                  if (raw === undefined || raw === null || raw === '') return raw;
                  try { return JSON.parse(raw); } catch { return raw; }
                };
                const normalizedConditions = (customRuleDraft.conditions || []).map(c => ({
                  field: c.field || '',
                  op: c.op || 'eq',
                  value: parseValue(c.value),
                }));
                const rule: AgentCustomRule = {
                  ...customRuleDraft,
                  id: customRuleDraft.id || 'test.rule',
                  conditions: normalizedConditions,
                  section: activeAgentModule === 'QUOTER' ? (customRuleDraft.section || 'ROUTE') : customRuleDraft.section,
                };
                const result = evaluateCustomRules([rule], context);
                setRuleTestResult(result.length > 0 ? 'MATCHED: Rule would trigger.' : 'NOT MATCHED: Rule would not trigger.');
              } catch (e: any) {
                setRuleTestResult(e.message || 'Invalid JSON');
              }
            }}
            className="px-6 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest bg-slate-900 text-white"
          >
            Test Rule
          </button>
          <button
            onClick={() => {
              setCustomRuleDraft({
                id: '',
                title: '',
                severity: 'MEDIUM',
                detail: '',
                suggestion: '',
                section: activeAgentModule === 'QUOTER' ? 'ROUTE' : undefined,
                enabled: true,
                logic: 'all',
                conditions: [{ field: '', op: 'eq', value: '' }],
              });
            }}
            className="px-6 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-400"
          >
            Clear
          </button>
        </div>
        {ruleTestResult && (
          <div className="px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">
            {ruleTestResult}
          </div>
        )}
        <div className="space-y-2">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Live Context Preview</p>
          <pre className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-[10px] text-slate-600 overflow-auto max-h-48 whitespace-pre-wrap break-words">
            {localStorage.getItem(`ma_agent_context_${activeAgentModule}`) || 'No live context available yet.'}
          </pre>
        </div>
        <div className="space-y-3">
          {(agentSettings.customRulesByModule?.[activeAgentModule] || []).map((rule: AgentCustomRule) => (
            <div key={rule.id} className="flex items-start justify-between p-4 border border-slate-200 rounded-2xl bg-white">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-700">{rule.title}</p>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                  Conditions: {rule.conditions?.length || 0}
                </p>
                {rule.section && (
                  <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest mt-1">
                    Section: {rule.section}
                  </p>
                )}
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{rule.severity}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const current = agentSettings.customRulesByModule?.[activeAgentModule] || [];
                    const next = current.map((r: AgentCustomRule) => r.id === rule.id ? { ...r, enabled: !r.enabled } : r);
                    setAgentSettings({
                      ...agentSettings,
                      customRulesByModule: { ...agentSettings.customRulesByModule, [activeAgentModule]: next },
                    });
                  }}
                  className={`px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest border ${
                    rule.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'
                  }`}
                >
                  {rule.enabled ? 'Enabled' : 'Disabled'}
                </button>
                <button
                  onClick={() => {
                    const current = agentSettings.customRulesByModule?.[activeAgentModule] || [];
                    const next = current.filter((r: AgentCustomRule) => r.id !== rule.id);
                    setAgentSettings({
                      ...agentSettings,
                      customRulesByModule: { ...agentSettings.customRulesByModule, [activeAgentModule]: next },
                    });
                  }}
                  className="px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest border bg-rose-50 text-rose-700 border-rose-200"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Custom Rules (JSON)</p>
          <textarea
            rows={6}
            value={customRulesJsonDraft}
            onChange={(e) => setCustomRulesJsonDraft(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 font-mono text-xs text-slate-700 outline-none"
          />
          {customRulesJsonError && (
            <p className="text-[9px] font-black uppercase tracking-widest text-rose-500">{customRulesJsonError}</p>
          )}
          <button
            onClick={() => {
              try {
                const parsed = JSON.parse(customRulesJsonDraft);
                if (!Array.isArray(parsed)) throw new Error('JSON must be an array of rules.');
                const errors = validateCustomRules(parsed);
                if (errors.length) {
                  setCustomRulesJsonError(errors[0]);
                  return;
                }
                setAgentSettings({
                  ...agentSettings,
                  customRulesByModule: {
                    ...agentSettings.customRulesByModule,
                    [activeAgentModule]: parsed,
                  },
                });
                setCustomRulesJsonError(null);
              } catch (e: any) {
                setCustomRulesJsonError(e.message || 'Invalid JSON');
              }
            }}
            className="px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest bg-slate-900 text-white"
          >
            Apply JSON
          </button>
          <button
            onClick={() => {
              const rules = agentSettings.customRulesByModule?.[activeAgentModule] || [];
              const blob = new Blob([JSON.stringify(rules, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `agent-rules-${activeAgentModule.toLowerCase()}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest bg-indigo-600 text-white"
          >
            Export JSON
          </button>
          <button
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'application/json';
              input.onchange = async () => {
                const file = input.files?.[0];
                if (!file) return;
                const text = await file.text();
                try {
                  const parsed = JSON.parse(text);
                  if (!Array.isArray(parsed)) throw new Error('JSON must be an array of rules.');
                  const errors = validateCustomRules(parsed);
                  if (errors.length) {
                    setCustomRulesJsonError(errors[0]);
                    return;
                  }
                  setAgentSettings({
                    ...agentSettings,
                    customRulesByModule: {
                      ...agentSettings.customRulesByModule,
                      [activeAgentModule]: parsed,
                    },
                  });
                  setCustomRulesJsonError(null);
                } catch (e: any) {
                  setCustomRulesJsonError(e.message || 'Invalid JSON');
                }
              };
              input.click();
            }}
            className="px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-500"
          >
            Import JSON
          </button>
        </div>
        <div className="space-y-3">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Agent History (Rollback)</p>
          <div className="space-y-2">
            {agentHistory.length === 0 && (
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-300">No history yet</p>
            )}
            {agentHistory.map(h => (
              <div key={h.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-2xl bg-white">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">{new Date(h.timestamp).toLocaleString()}</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{h.userEmail || 'system'}</p>
                </div>
                <button
                  onClick={() => onRollbackSnapshot(h.id)}
                  className="px-4 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest bg-rose-50 text-rose-700 border border-rose-200"
                >
                  Rollback
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10 space-y-10 min-w-0">
      <div className="border-b border-slate-50 pb-6">
        <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em]">Guidance Layer</h3>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Global prompt behavior and output controls</p>
      </div>

      <div className="rounded-[2rem] border border-slate-100 bg-slate-50 p-5 space-y-4">
        <div>
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">AI Configuration Moved</p>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Provider connections, API keys, models, and per-task LLM routing now live under the dedicated <strong>AI</strong> tab. This tab stays focused on behavior, rules, and governance.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() =>
            setAgentSettings({
              ...agentSettings,
              guidance: { ...agentSettings.guidance, includeChecklist: !agentSettings.guidance.includeChecklist },
            })
          }
          className={`px-6 py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
            agentSettings.guidance.includeChecklist
              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
              : 'bg-slate-50 text-slate-400 border border-slate-100'
          }`}
        >
          Checklist Guidance
        </button>
        <button
          onClick={() =>
            setAgentSettings({
              ...agentSettings,
              guidance: { ...agentSettings.guidance, includePolicyRefs: !agentSettings.guidance.includePolicyRefs },
            })
          }
          className={`px-6 py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
            agentSettings.guidance.includePolicyRefs
              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
              : 'bg-slate-50 text-slate-400 border border-slate-100'
          }`}
        >
          Policy References
        </button>
      </div>

      <div className="space-y-3">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Global Knowledge Base Notes</label>
        <textarea
          rows={4}
          value={agentSettings.guidance.knowledgeBaseNotes}
          onChange={(e) =>
            setAgentSettings({
              ...agentSettings,
              guidance: { ...agentSettings.guidance, knowledgeBaseNotes: e.target.value },
            })
          }
          placeholder="General SOP notes applied to all modules"
          className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none"
        />
      </div>
    </section>
  </div>
);
};
